import { keyframes } from '@mui/material/styles';
import { useCallback, useEffect, useRef, useState } from 'react';
import { isJobIntegrationLiveNotification, subscribeLiveNotifications } from './liveNotifications';

export const LIVE_UPDATE_HIGHLIGHT_MS = 1800;

export const liveUpdateHighlightPulse = keyframes`
  0% {
    transform: scale(1.02);
    box-shadow: 0 0 0 3px rgba(33, 150, 243, 0.5);
    background-color: rgba(33, 150, 243, 0.16);
  }
  40% {
    transform: scale(1.01);
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.3);
    background-color: rgba(33, 150, 243, 0.1);
  }
  100% {
    transform: scale(1);
    box-shadow: none;
    background-color: transparent;
  }
`;

export function liveUpdateHighlightSx(highlighted) {
    if (!highlighted) {
        return {};
    }

    return {
        animation: `${liveUpdateHighlightPulse} ${LIVE_UPDATE_HIGHLIGHT_MS}ms ease-out`,
        transformOrigin: 'center center',
        position: 'relative',
        zIndex: 1,
        transition: 'border-color 0.3s ease',
    };
}

export function liveUpdateHighlightRowSx(highlighted) {
    if (!highlighted) {
        return {};
    }

    return {
        ...liveUpdateHighlightSx(highlighted),
        '& > td': {
            backgroundColor: 'inherit',
        },
    };
}

export function useLiveUpdateHighlights(matcher = isJobIntegrationLiveNotification) {
    const [printerHighlights, setPrinterHighlights] = useState(() => new Set());
    const [jobHighlights, setJobHighlights] = useState(() => new Set());
    const timersRef = useRef([]);

    const scheduleClear = useCallback((type, id) => {
        const timer = window.setTimeout(() => {
            if (type === 'printer') {
                setPrinterHighlights((prev) => {
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
            } else {
                setJobHighlights((prev) => {
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
            }
        }, LIVE_UPDATE_HIGHLIGHT_MS);

        timersRef.current.push(timer);
    }, []);

    useEffect(() => {
        return subscribeLiveNotifications((detail) => {
            if (!matcher(detail)) {
                return;
            }

            if (detail.printerUuid) {
                setPrinterHighlights((prev) => new Set(prev).add(detail.printerUuid));
                scheduleClear('printer', detail.printerUuid);
            }

            if (detail.jobUuid) {
                setJobHighlights((prev) => new Set(prev).add(detail.jobUuid));
                scheduleClear('job', detail.jobUuid);
            }
        });
    }, [matcher, scheduleClear]);

    useEffect(() => () => {
        timersRef.current.forEach((timer) => window.clearTimeout(timer));
        timersRef.current = [];
    }, []);

    const isPrinterHighlighted = useCallback(
        (printerUuid) => printerHighlights.has(printerUuid),
        [printerHighlights],
    );

    const isJobHighlighted = useCallback(
        (jobUuid) => jobHighlights.has(jobUuid),
        [jobHighlights],
    );

    return { isPrinterHighlighted, isJobHighlighted };
}
